import { Box, BoxProps } from '@chakra-ui/react';
import { Tooltip } from '../ui/tooltip';
import React, { FunctionComponent as FC } from 'react';
import { useHover } from '../../hooks';

interface MCustomBadgeProps extends BoxProps {
  text?: string;
  tooltipLabel?: string;
  /** If provided, the badge turns into an `x` icon and allows clicking, which should clear the items */
  onClickReset?: () => void;
  children: any;
}

const MCustomBadge: FC<MCustomBadgeProps> = React.forwardRef<
  any,
  MCustomBadgeProps
>(
  (
    {
      text,
      tooltipLabel,
      onClickReset,
      children,
      // `onClick` excluded: when this renders as a `Popover.Trigger`/`Menu.Trigger`
      // `asChild` target, the caller's actual interactive child (e.g. an icon
      // button) already owns its own onClick wired to the caller's open state.
      // Forwarding the injected onClick here too would double-fire on click
      // (bubbling from the inner element up to this wrapper), toggling open
      // then immediately closed. Every other injected prop (id, aria-*,
      // data-*) is still forwarded — those are needed for correct a11y
      // wiring and so floating-ui can find this element as the trigger.
      onClick: _onClick,
      ...rest
    }: MCustomBadgeProps,
    ref,
  ) => {
    const [hoveredRef, isHovered] = useHover<HTMLDivElement>();

    const handleReset = () => {
      onClickReset && onClickReset();
    };

    return (
      <Box ref={ref} {...rest}>
        {children}

        {!!text && (
          <Tooltip content={tooltipLabel}>
            <Box
              ref={hoveredRef}
              backgroundColor="tRed.base"
              position="absolute"
              top="-2"
              right="-1"
              borderRadius="50%"
              width="5"
              height="5"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="white"
              fontSize="xs"
              fontWeight="bold"
              borderWidth="2px"
              borderColor="white"
              _hover={onClickReset ? { cursor: 'pointer' } : undefined}
              onClick={handleReset}
            >
              {/* Cannot use icon here because isHovered changes state */}
              {onClickReset && isHovered ? 'x' : text}
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  },
);

export default MCustomBadge;
